import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit2, Trash2, Package, Clock, DollarSign, 
  X, Save, Loader2, ToggleLeft, ToggleRight 
} from 'lucide-react';
import './AdminServices.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    turnaround_days: 1,
    price: '',
    is_active: true,
    special_rules: { blocks_week: false, max_per_day: null }
  });

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/services`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setServices(data);
      }
    } catch (error) {
      console.error('Failed to fetch services:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingService(null);
    setFormData({
      name: '',
      slug: '',
      description: '',
      turnaround_days: 1,
      price: '',
      is_active: true,
      special_rules: { blocks_week: false, max_per_day: null }
    });
    setShowModal(true);
  };

  const openEditModal = (service) => {
    setEditingService(service);
    setFormData({
      name: service.name,
      slug: service.slug,
      description: service.description,
      turnaround_days: service.turnaround_days,
      price: service.price,
      is_active: service.is_active,
      special_rules: service.special_rules || { blocks_week: false, max_per_day: null }
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('adminToken');
      const url = editingService 
        ? `${API_URL}/api/admin/services/${editingService.id}`
        : `${API_URL}/api/admin/services`;
      
      const response = await fetch(url, {
        method: editingService ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          slug: formData.slug || formData.name.toLowerCase().replace(/\s+/g, '-')
        })
      });

      if (response.ok) {
        fetchServices();
        setShowModal(false);
      }
    } catch (error) {
      console.error('Failed to save service:', error);
    } finally {
      setSaving(false);
    }
  };

  const deleteService = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/services/${serviceId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchServices();
      }
    } catch (error) {
      console.error('Failed to delete service:', error);
    }
  };

  const toggleServiceStatus = async (service) => {
    try {
      const token = localStorage.getItem('adminToken');
      await fetch(`${API_URL}/api/admin/services/${service.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: !service.is_active })
      });
      fetchServices();
    } catch (error) {
      console.error('Failed to toggle service status:', error);
    }
  };

  return (
    <div className="admin-services" data-testid="admin-services">
      <div className="page-header">
        <div>
          <h1>Services</h1>
          <p>Manage your service offerings and pricing</p>
        </div>
        <button className="add-btn" onClick={openCreateModal} data-testid="add-service-btn">
          <Plus size={18} />
          <span>Add Service</span>
        </button>
      </div>

      {loading ? (
        <div className="loading-container">
          <Loader2 className="spin" size={32} />
          <span>Loading services...</span>
        </div>
      ) : services.length === 0 ? (
        <div className="empty-container">
          <Package size={48} />
          <h3>No services yet</h3>
          <p>Create your first service to get started.</p>
          <button className="add-btn" onClick={openCreateModal}>
            <Plus size={18} />
            <span>Add Service</span>
          </button>
        </div>
      ) : (
        <div className="services-grid">
          {services.map((service) => (
            <div 
              key={service.id} 
              className={`service-card ${!service.is_active ? 'inactive' : ''}`}
              data-testid={`service-card-${service.id}`}
            >
              <div className="service-header">
                <div className="service-status">
                  <button 
                    className="toggle-btn" 
                    onClick={() => toggleServiceStatus(service)}
                    title={service.is_active ? 'Active - Click to deactivate' : 'Inactive - Click to activate'}
                  >
                    {service.is_active ? (
                      <ToggleRight size={24} className="toggle-on" />
                    ) : (
                      <ToggleLeft size={24} className="toggle-off" />
                    )}
                  </button>
                </div>
                <div className="service-actions">
                  <button 
                    className="action-btn edit" 
                    onClick={() => openEditModal(service)}
                    data-testid={`edit-service-${service.id}`}
                  >
                    <Edit2 size={16} />
                  </button>
                  <button 
                    className="action-btn delete" 
                    onClick={() => deleteService(service.id)}
                    data-testid={`delete-service-${service.id}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              <h3 className="service-name">{service.name}</h3>
              <p className="service-description">{service.description}</p>

              <div className="service-meta">
                <div className="meta-item">
                  <Clock size={16} />
                  <span>{service.turnaround_days} day{service.turnaround_days > 1 ? 's' : ''}</span>
                </div>
                <div className="meta-item price">
                  <DollarSign size={16} />
                  <span>{service.price}</span>
                </div>
              </div>

              {service.special_rules?.blocks_week && (
                <div className="special-badge">Blocks entire week</div>
              )}
              {service.special_rules?.max_per_day && (
                <div className="special-badge">Max {service.special_rules.max_per_day}/day</div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Service Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} data-testid="service-modal">
            <div className="modal-header">
              <h2>{editingService ? 'Edit Service' : 'Add New Service'}</h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="service-form">
              <div className="form-group">
                <label>Service Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Wedding Film"
                  required
                  data-testid="service-name-input"
                />
              </div>

              <div className="form-group">
                <label>Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe your service..."
                  rows={3}
                  required
                  data-testid="service-description-input"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Turnaround (days) *</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.turnaround_days}
                    onChange={(e) => setFormData({ ...formData, turnaround_days: parseInt(e.target.value) })}
                    required
                    data-testid="service-turnaround-input"
                  />
                </div>
                <div className="form-group">
                  <label>Price *</label>
                  <input
                    type="text"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="Starting from ₹10,000"
                    required
                    data-testid="service-price-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Special Rules</label>
                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.special_rules.blocks_week}
                      onChange={(e) => setFormData({
                        ...formData,
                        special_rules: { ...formData.special_rules, blocks_week: e.target.checked }
                      })}
                    />
                    <span>Blocks entire week</span>
                  </label>
                </div>
                <div className="form-group-sm">
                  <label>Max per day (leave empty for no limit)</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.special_rules.max_per_day || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      special_rules: { 
                        ...formData.special_rules, 
                        max_per_day: e.target.value ? parseInt(e.target.value) : null 
                      }
                    })}
                    placeholder="e.g., 3"
                  />
                </div>
              </div>

              <div className="form-actions">
                <button 
                  type="button" 
                  className="btn-cancel" 
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-save" 
                  disabled={saving}
                  data-testid="save-service-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 size={16} className="spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      <span>{editingService ? 'Update' : 'Create'} Service</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminServices;
